import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2, Package, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AssetThumb } from "@/components/assets/AssetThumb";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const AssetGallery = ({ a, canEdit }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const ids = a.photo_ids || [];
  const shown = ids.includes(sel) ? sel : a.photo_id;
  const save = async (body, msg) => {
    try {
      await api.patch(`/households/${active.id}/assets/${a.id}`, body);
      qc.invalidateQueries({ queryKey: ["asset", active.id, a.id] });
      qc.invalidateQueries({ queryKey: ["assets", active.id] });
      if (msg) toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/households/${active.id}/assets/photo?analyze=false`, fd);
      await save({ photo_ids: [...ids, data.photo_id], photo_id: a.photo_id || data.photo_id }, "Photo ajoutée");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = (id) => save({ photo_ids: ids.filter((x) => x !== id), photo_id: a.photo_id === id ? ids.find((x) => x !== id) || null : a.photo_id }, "Photo retirée");
  return (
    <div className="space-y-3" data-testid="asset-gallery">
      <div className="relative">
        <AssetThumb photoId={shown} icon={Package} className="aspect-[4/3] w-full rounded-2xl" />
        {canEdit && shown && (
          <div className="absolute right-3 top-3 flex gap-2">
            {shown !== a.photo_id && <button onClick={() => save({ photo_id: shown }, "Photo principale changée")} className="flex items-center gap-1 rounded-full bg-card/90 px-3 py-1.5 text-xs font-semibold shadow" data-testid="gallery-set-main"><Star className="h-3.5 w-3.5" />Principale</button>}
            <button onClick={() => remove(shown)} className="flex h-8 w-8 items-center justify-center rounded-full bg-card/90 text-destructive shadow" aria-label="Retirer la photo" data-testid="gallery-remove"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {ids.map((id, i) => (
          <button key={id} onClick={() => setSel(id)} className={cn("relative rounded-xl ring-2 ring-offset-2 ring-offset-background transition-transform hover:scale-105", id === shown ? "ring-primary" : "ring-transparent")} data-testid={`gallery-thumb-${i}`}>
            <AssetThumb photoId={id} icon={Package} className="h-16 w-16 rounded-xl" />
            {id === a.photo_id && <Star className="absolute left-1 top-1 h-3.5 w-3.5 fill-[#D8BD78] text-[#D8BD78]" />}
          </button>
        ))}
        {canEdit && ids.length < 6 && (
          <>
            <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="gallery-input" />
            <button onClick={() => ref.current?.click()} disabled={busy} className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-xl border border-dashed border-border text-[10px] text-muted-foreground hover:bg-secondary" data-testid="gallery-add">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{ids.length}/6
            </button>
          </>
        )}
      </div>
    </div>
  );
};
